/**
 * Publicidade própria (house ads) do placement da grade da Home.
 *
 * Ordem do placement (AnuncioGrade): o Google AdSense é sempre a primeira opção. Só quando
 * o bloco do Google fica comprovadamente vazio entra um destes anúncios internos, no mesmo
 * espaço. Nunca os dois ao mesmo tempo.
 *
 * Campanhas ficam aqui, estruturadas, e não no JSX. Quando existir /sistema/publicidade,
 * esta lista passa a vir da API com o mesmo formato:
 *
 *   campaign_id            id estável, vai nos eventos house_ad_impression/house_ad_click
 *   store_id               slug canônico da loja (MARKETPLACES / _CLICK_MARKETPLACE_HOSTS)
 *   titulo, subtitulo, cta textos simples; sem desconto ou preço que não dê para comprovar
 *   destination            rota INTERNA do Super Vitrine (começa com "/"). A pessoa vê as
 *                          ofertas da loja aqui dentro; a saída para a loja acontece no card
 *                          da oferta, pelo redirector /r/{deal_id}, que aplica a afiliação.
 *   affiliate_destination  reservado. null: nenhum anúncio interno leva direto à loja nem
 *                          carrega URL externa no frontend.
 *   imagem                 banner opcional (arquivo do próprio projeto); null usa o logo oficial
 *   enabled, priority      liga/desliga e ordem (maior primeiro)
 *   start_at, end_at       período em ISO 8601; null = sem limite
 *
 * Nome e logo vêm de MARKETPLACES (wordmark oficial já usado em "Explorar por loja").
 *
 * Lojas escolhidas pelo que o catálogo sustenta em 2026-09-17 (ofertas compráveis na API
 * pública): Mercado Livre 4101, Shopee 2468, Amazon 249. Magalu (1 oferta) ficou de fora:
 * o anúncio levaria a uma página quase vazia.
 */
import { MARKETPLACES, buscaPorMarketplace } from '../design/marketplaces';

export const PLACEMENT_GRADE = 'home_feed';

// A cada 3 placements sem anúncio do Google, 1 continua sendo o atalho de busca do site
// ("Procurando algo específico?"): nem todo espaço utilitário vira publicidade.
export const CICLO_BUSCA = 3;

export const HOUSE_ADS = [
    {
        campaign_id: 'house_mercadolivre_ofertas',
        store_id: 'mercadolivre',
        titulo: 'Ofertas do Mercado Livre',
        subtitulo: 'Encontre promoções selecionadas pelo Super Vitrine.',
        cta: 'Ver ofertas',
        destination: buscaPorMarketplace('mercadolivre'),
        affiliate_destination: null,
        imagem: null,
        enabled: true,
        priority: 30,
        start_at: null,
        end_at: null,
    },
    {
        campaign_id: 'house_shopee_ofertas',
        store_id: 'shopee',
        titulo: 'Ofertas da Shopee',
        subtitulo: 'Encontre promoções selecionadas pelo Super Vitrine.',
        cta: 'Ver ofertas',
        destination: buscaPorMarketplace('shopee'),
        affiliate_destination: null,
        imagem: null,
        enabled: true,
        priority: 20,
        start_at: null,
        end_at: null,
    },
    {
        campaign_id: 'house_amazon_ofertas',
        store_id: 'amazon',
        titulo: 'Ofertas da Amazon',
        subtitulo: 'Encontre promoções selecionadas pelo Super Vitrine.',
        cta: 'Ver ofertas',
        destination: buscaPorMarketplace('amazon'),
        affiliate_destination: null,
        imagem: null,
        enabled: true,
        priority: 10,
        start_at: null,
        end_at: null,
    },
];

const RE_CAMPANHA = /^[a-z0-9][a-z0-9_-]{2,59}$/;
const PALAVRAS_PROIBIDAS = /google|adsense/i;

const rotaInterna = (valor) => typeof valor === 'string' && valor.startsWith('/') && !valor.startsWith('//')
    && !/^\/[^?#]*:/.test(valor);

const data = (valor) => {
    if (valor === null || valor === undefined) return null;
    const t = Date.parse(valor);
    return Number.isNaN(t) ? NaN : t;
};

/** Motivo pelo qual a campanha não pode ir ao ar, ou null se está válida. Não olha o período. */
export function motivoInvalida(c) {
    if (!c || typeof c !== 'object') return 'formato';
    if (!RE_CAMPANHA.test(c.campaign_id || '')) return 'campaign_id';
    if (!MARKETPLACES.some((m) => m.slug === c.store_id)) return 'loja_nao_integrada';
    if (!c.titulo || !c.cta) return 'texto';
    if ([c.titulo, c.subtitulo, c.cta].some((t) => PALAVRAS_PROIBIDAS.test(t || ''))) return 'identidade_google';
    if (!rotaInterna(c.destination)) return 'destino_externo';
    if (c.affiliate_destination !== null && c.affiliate_destination !== undefined) return 'afiliado_direto';
    if (c.imagem !== null && c.imagem !== undefined && !rotaInterna(c.imagem)) return 'imagem_externa';
    if (Number.isNaN(data(c.start_at)) || Number.isNaN(data(c.end_at))) return 'periodo';
    return null;
}

/** Campanhas válidas, ligadas e dentro do período, da maior prioridade para a menor. */
export function campanhasAtivas(agora = Date.now(), lista = HOUSE_ADS) {
    return lista
        .filter((c) => c && c.enabled === true && motivoInvalida(c) === null)
        .filter((c) => {
            const ini = data(c.start_at);
            const fim = data(c.end_at);
            return (ini === null || ini <= agora) && (fim === null || agora < fim);
        })
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));
}

/**
 * Qual anúncio interno ocupa o placement `posicao` (0, 1, 2...) quando o Google não
 * preenche. null = atalho de busca. Determinístico: a mesma posição mostra sempre a mesma
 * campanha na mesma lista, então re-render e "carregar mais" não trocam o que já foi visto.
 */
export function campanhaDoPlacement(posicao, agora = Date.now(), lista = HOUSE_ADS) {
    if (!Number.isInteger(posicao) || posicao < 0) return null;
    if (posicao % CICLO_BUSCA === CICLO_BUSCA - 1) return null;
    const ativas = campanhasAtivas(agora, lista);
    if (!ativas.length) return null;
    const indice = posicao - Math.floor((posicao + 1) / CICLO_BUSCA);
    return ativas[indice % ativas.length];
}

export const lojaDaCampanha = (c) => MARKETPLACES.find((m) => m.slug === c?.store_id) || null;
